import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container, Typography, Box, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Tooltip, CircularProgress, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, Stack, Card, CardContent, Grid, Select, MenuItem, FormControl, InputLabel, InputAdornment, ToggleButton, ToggleButtonGroup,
    Chip, Avatar, Divider
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, ContentCopy as ContentCopyIcon, Check as CheckIcon, ViewList as ViewListIcon, ViewModule as ViewModuleIcon, Place as PlaceIcon, Person as PersonIcon, Event as EventIcon } from '@mui/icons-material';
import { getAllItineraryNotes, createItineraryNote, updateItineraryNote, deleteItineraryNote } from '../../api/destinationItineraryStoreAPI';
import useSnackbar from '../../hooks/useSnackbar';
import usePermissions from '../../hooks/UsePermissions';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import parse from 'html-react-parser';

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link'],
        ['clean'],
    ],
    clipboard: {
        matchVisual: false,
    },
};

const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent',
    'align', 'link',
];

const DestinationItineraryStore = () => {
    const { showSnackbar, SnackbarComponent } = useSnackbar();
    const hasPermission = usePermissions();

    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [filterDestination, setFilterDestination] = useState("");
    const [filterSubDestination, setFilterSubDestination] = useState("");
    const [allDestinations, setAllDestinations] = useState([]);
    const [allSubDestinations, setAllSubDestinations] = useState([]);

    const [viewMode, setViewMode] = useState("list");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const [currentNote, setCurrentNote] = useState(null);
    const [copied, setCopied] = useState(false);
    const viewContentRef = useRef(null);
    const emptyDay = { headerTag: '', headerValue: '', Distance: '', Duration: '', Altitude: '', Time: '', para: [''] };
    const [formData, setFormData] = useState({ 
        title: '', destination: '', sub_destination: '', itinerary: [emptyDay] 
    });

    const fetchNotes = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAllItineraryNotes({
                searchQuery,
                destination: filterDestination,
                subDestination: filterSubDestination
            });
            if (res.success) {
                setNotes(res.data);
            }
        } catch (error) {
            console.error("Error fetching notes:", error);
            showSnackbar("Failed to fetch itineraries", "error");
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, filterDestination, filterSubDestination]);

    // Initial load for unique destinations for filters
    useEffect(() => {
        (async () => {
            try {
                const res = await getAllItineraryNotes({});
                if (res.success && res.data) {
                    setAllDestinations([...new Set(res.data.map(n => n.destination).filter(Boolean))]);
                    setAllSubDestinations([...new Set(res.data.map(n => n.sub_destination).filter(Boolean))]);
                }
            } catch (err) {}
        })();
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchNotes();
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [fetchNotes]);

    const handleOpenDialog = (note = null) => {
        if (note) {
            setCurrentNote(note);
            setFormData({ 
                title: note.title, 
                destination: note.destination || '',
                sub_destination: note.sub_destination || '',
                itinerary: note.itinerary && note.itinerary.length > 0 ? note.itinerary : [emptyDay] 
            });
        } else {
            setCurrentNote(null);
            setFormData({ title: '', destination: '', sub_destination: '', itinerary: [emptyDay] });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setCurrentNote(null);
    };

    const handleSave = async () => {
        if (!formData.title.trim() || !formData.destination.trim()) {
            showSnackbar("Title and Destination are required", "warning");
            return;
        }

        const dataToSave = {
            ...formData,
            itinerary: formData.itinerary
        };

        try {
            if (currentNote) {
                await updateItineraryNote(currentNote._id, dataToSave);
                showSnackbar("Itinerary updated successfully", "success");
            } else {
                await createItineraryNote(dataToSave);
                showSnackbar("Itinerary created successfully", "success");
            }
            handleCloseDialog();
            fetchNotes();
            
            // Refresh unique destinations
            setAllDestinations([...new Set([...allDestinations, formData.destination])]);
            if (formData.sub_destination) setAllSubDestinations([...new Set([...allSubDestinations, formData.sub_destination])]);
            
        } catch (error) {
            console.error("Error saving note:", error);
            showSnackbar(error.response?.data?.message || "Failed to save itinerary", "error");
        }
    };

    const handleOpenView = (note) => {
        setCurrentNote(note);
        setViewDialogOpen(true);
    };

    const handleOpenDelete = (note) => {
        setCurrentNote(note);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!currentNote) return;
        try {
            await deleteItineraryNote(currentNote._id);
            showSnackbar("Itinerary deleted successfully", "success");
            setDeleteDialogOpen(false);
            fetchNotes();
        } catch (error) {
            console.error("Error deleting note:", error);
            showSnackbar("Failed to delete itinerary", "error");
        }
    };

    const addDay = () => setFormData({ ...formData, itinerary: [...formData.itinerary, emptyDay] });
    const removeDay = (index) => {
        const newItinerary = [...formData.itinerary];
        newItinerary.splice(index, 1);
        setFormData({ ...formData, itinerary: newItinerary });
    };

    const handleItineraryChange = (index, field, value) => {
        const newItinerary = [...formData.itinerary];
        newItinerary[index][field] = value;
        setFormData({ ...formData, itinerary: newItinerary });
    };

    const addParagraph = (dayIndex) => {
        const newItinerary = [...formData.itinerary];
        newItinerary[dayIndex].para.push('');
        setFormData({ ...formData, itinerary: newItinerary });
    };
    const removeParagraph = (dayIndex, paraIndex) => {
        const newItinerary = [...formData.itinerary];
        newItinerary[dayIndex].para.splice(paraIndex, 1);
        setFormData({ ...formData, itinerary: newItinerary });
    };

    const handleParagraphChange = (dayIndex, paraIndex, value) => {
        const newItinerary = [...formData.itinerary];
        newItinerary[dayIndex].para[paraIndex] = value;
        setFormData({ ...formData, itinerary: newItinerary });
    };

    const handleCopyItinerary = async () => {
        if (!currentNote || !viewContentRef.current) return;
        try {
            let htmlContent = `<div style="font-family: Arial, Helvetica, sans-serif; color: #0f172a;">`;
            currentNote.itinerary?.forEach((day) => {
                htmlContent += `<div style="margin-bottom: 24px;">`;
                const headerText = day.headerValue || '';
                htmlContent += `<h3 style="font-size: 18px; font-weight: bold; margin-bottom: 12px; margin-top: 0; color: #1e293b;">${headerText}</h3>`;
                
                if (day.Distance || day.Duration || day.Altitude || day.Time) {
                    htmlContent += `<div style="font-size: 14px; margin-bottom: 12px; color: #475569;">`;
                    if (day.Distance) htmlContent += `<span style="margin-right: 16px;"><strong>Distance:</strong> ${day.Distance}</span>`;
                    if (day.Duration) htmlContent += `<span style="margin-right: 16px;"><strong>Duration:</strong> ${day.Duration}</span>`;
                    if (day.Altitude) htmlContent += `<span style="margin-right: 16px;"><strong>Altitude:</strong> ${day.Altitude}</span>`;
                    if (day.Time) htmlContent += `<span><strong>Time:</strong> ${day.Time}</span>`;
                    htmlContent += `</div>`;
                }

                if (day.para && day.para.length > 0) {
                    day.para.forEach((p) => {
                        if (p) htmlContent += `<div style="font-size: 15px; line-height: 1.6; margin-bottom: 12px;">${p}</div>`;
                    });
                }
                htmlContent += `</div>`;
            });
            htmlContent += `</div>`;

            const blob = new Blob([htmlContent], { type: "text/html" });
            const plainText = currentNote.itinerary?.map(d => `${d.headerValue || ''}\n${(d.para || []).join('\n')}`).join('\n\n') || '';
            const plainBlob = new Blob([plainText], { type: "text/plain" });
            
            const data = [new ClipboardItem({
                "text/html": blob,
                "text/plain": plainBlob
            })];

            await navigator.clipboard.write(data);
            showSnackbar("Full itinerary copied to clipboard!", "success");
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (err) {
            console.error("Failed to copy", err);
            showSnackbar("Failed to copy itinerary", "error");
        }
    };

    const handleCopySingleItinerary = async (day) => {
        try {
            let htmlContent = `<div style="font-family: Arial, Helvetica, sans-serif; color: #0f172a;">`;
            const headerText = day.headerValue || '';
            htmlContent += `<h3 style="font-size: 18px; font-weight: bold; margin-bottom: 12px; margin-top: 0; color: #1e293b;">${headerText}</h3>`;
            
            if (day.Distance || day.Duration || day.Altitude || day.Time) {
                htmlContent += `<div style="font-size: 14px; margin-bottom: 12px; color: #475569;">`;
                if (day.Distance) htmlContent += `<span style="margin-right: 16px;"><strong>Distance:</strong> ${day.Distance}</span>`;
                if (day.Duration) htmlContent += `<span style="margin-right: 16px;"><strong>Duration:</strong> ${day.Duration}</span>`;
                if (day.Altitude) htmlContent += `<span style="margin-right: 16px;"><strong>Altitude:</strong> ${day.Altitude}</span>`;
                if (day.Time) htmlContent += `<span><strong>Time:</strong> ${day.Time}</span>`;
                htmlContent += `</div>`;
            }

            if (day.para && day.para.length > 0) {
                day.para.forEach((p) => {
                    if (p) htmlContent += `<div style="font-size: 15px; line-height: 1.6; margin-bottom: 12px;">${p}</div>`;
                });
            }
            htmlContent += `</div>`;

            const blob = new Blob([htmlContent], { type: "text/html" });
            const plainText = `${day.headerValue || ''}\n${(day.para || []).join('\n')}`;
            const plainBlob = new Blob([plainText], { type: "text/plain" });
            
            const data = [new ClipboardItem({
                "text/html": blob,
                "text/plain": plainBlob
            })];

            await navigator.clipboard.write(data);
            showSnackbar("Day itinerary copied to clipboard!", "success");
        } catch (err) {
            console.error("Failed to copy day", err);
            showSnackbar("Failed to copy day itinerary", "error");
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" color="primary" fontWeight={800}>
                    Destination-Based Itinerary Store
                </Typography>
                {hasPermission("destination-itinerary-store", "create") && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add New Itinerary
                    </Button>
                )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                    <TextField size="small" label="Search by Title" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Destination</InputLabel>
                        <Select value={filterDestination} onChange={(e) => setFilterDestination(e.target.value)} label="Destination">
                            <MenuItem value="">All</MenuItem>
                            {allDestinations.map((d) => (<MenuItem key={d} value={d}>{d}</MenuItem>))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Sub Destination</InputLabel>
                        <Select value={filterSubDestination} onChange={(e) => setFilterSubDestination(e.target.value)} label="Sub Destination">
                            <MenuItem value="">All</MenuItem>
                            {allSubDestinations.map((d) => (<MenuItem key={d} value={d}>{d}</MenuItem>))}
                        </Select>
                    </FormControl>
                </Stack>
                <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                    size="small"
                >
                    <ToggleButton value="list" aria-label="list view">
                        <Tooltip title="List View"><ViewListIcon /></Tooltip>
                    </ToggleButton>
                    <ToggleButton value="card" aria-label="card view">
                        <Tooltip title="Card View"><ViewModuleIcon /></Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
            ) : notes.length === 0 ? (
                <Typography align="center" py={5} color="text.secondary">No itineraries found.</Typography>
            ) : viewMode === 'list' ? (
                <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2 }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Destination</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Sub Destination</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {notes.map((note) => (
                                <TableRow key={note._id} hover>
                                    <TableCell sx={{ fontWeight: 500 }}>{note.title}</TableCell>
                                    <TableCell>{note.destination}</TableCell>
                                    <TableCell>{note.sub_destination}</TableCell>
                                    <TableCell>{note.created_by?.fullName || "Unknown"}</TableCell>
                                    <TableCell>{new Date(note.created_at).toLocaleDateString()}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="View"><IconButton color="primary" onClick={() => handleOpenView(note)}><VisibilityIcon /></IconButton></Tooltip>
                                        {hasPermission("destination-itinerary-store", "alter") && (
                                            <Tooltip title="Edit"><IconButton color="secondary" onClick={() => handleOpenDialog(note)}><EditIcon /></IconButton></Tooltip>
                                        )}
                                        {hasPermission("destination-itinerary-store", "delete") && (
                                            <Tooltip title="Delete"><IconButton color="error" onClick={() => handleOpenDelete(note)}><DeleteIcon /></IconButton></Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Grid container spacing={3}>
                    {notes.map((note) => (
                        <Grid item xs={12} sm={6} md={4} key={note._id}>
                            <Card 
                                elevation={0} 
                                sx={{ 
                                    borderRadius: 4, 
                                    height: '100%', 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    border: '1px solid #e2e8f0',
                                    transition: 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.4s ease',
                                    position: 'relative',
                                    overflow: 'visible',
                                    bgcolor: '#ffffff',
                                    '&:hover': { 
                                        transform: 'translateY(-12px) scale(1.02)', 
                                        boxShadow: '0 30px 60px -15px rgba(59, 130, 246, 0.25)',
                                        borderColor: 'transparent',
                                        zIndex: 10
                                    } 
                                }}
                            >
                                {/* Vivid Header Section */}
                                <Box sx={{ 
                                    p: 3, 
                                    background: 'linear-gradient(135deg, #0f172a 0%, #3b82f6 100%)',
                                    borderTopLeftRadius: 16,
                                    borderTopRightRadius: 16,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    minHeight: 120,
                                    display: 'flex',
                                    alignItems: 'flex-start'
                                }}>
                                    {/* Decorative glowing blob */}
                                    <Box sx={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', filter: 'blur(24px)' }} />
                                    
                                    <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 800, lineHeight: 1.4, position: 'relative', zIndex: 1, textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
                                        {note.title}
                                    </Typography>
                                </Box>
                                
                                <CardContent sx={{ flexGrow: 1, pt: 4, pb: 2, px: 3, position: 'relative' }}>
                                    {/* Floating Primary Action Button */}
                                    <Tooltip title="View Detailed Itinerary" placement="top">
                                        <IconButton 
                                            sx={{ 
                                                position: 'absolute', 
                                                top: -24, 
                                                right: 24, 
                                                bgcolor: '#10b981', 
                                                color: '#fff', 
                                                width: 48,
                                                height: 48,
                                                boxShadow: '0 8px 20px rgba(16, 185, 129, 0.4)',
                                                '&:hover': { bgcolor: '#059669', transform: 'scale(1.1)', boxShadow: '0 12px 28px rgba(16, 185, 129, 0.6)' },
                                                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                                            }}
                                            onClick={() => handleOpenView(note)}
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>
                                    
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                                        <Chip 
                                            icon={<PlaceIcon sx={{ fontSize: 16, color: '#2563eb !important' }} />} 
                                            label={note.destination} 
                                            size="small" 
                                            sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700, borderRadius: 2, mb: 1, border: '1px solid #bfdbfe' }} 
                                        />
                                        {note.sub_destination && (
                                            <Chip 
                                                label={note.sub_destination} 
                                                size="small" 
                                                sx={{ bgcolor: '#f8fafc', color: '#475569', fontWeight: 600, borderRadius: 2, mb: 1, border: '1px solid #e2e8f0' }} 
                                            />
                                        )}
                                    </Stack>

                                    <Divider sx={{ mb: 2.5, borderStyle: 'dashed', borderColor: '#cbd5e1' }} />
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ width: 40, height: 40, background: 'linear-gradient(135deg, #f43f5e 0%, #fb923c 100%)', color: '#fff', fontWeight: 800, fontSize: '1.1rem', boxShadow: '0 4px 12px rgba(244, 63, 94, 0.3)' }}>
                                                {note.created_by?.fullName?.charAt(0) || 'U'}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" sx={{ display: 'block', fontWeight: 800, color: '#1e293b' }}>
                                                    {note.created_by?.fullName || "Unknown"}
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b', fontWeight: 500 }}>
                                                    <EventIcon sx={{ fontSize: 14 }} /> {new Date(note.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </CardContent>

                                {(hasPermission("destination-itinerary-store", "alter") || hasPermission("destination-itinerary-store", "delete")) && (
                                    <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottomLeftRadius: 16, borderBottomRightRadius: 16, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                        {hasPermission("destination-itinerary-store", "alter") && (
                                            <Button size="small" startIcon={<EditIcon />} sx={{ color: '#6366f1', fontWeight: 700, '&:hover': { bgcolor: '#e0e7ff' }, borderRadius: 2 }} onClick={() => handleOpenDialog(note)}>
                                                Edit
                                            </Button>
                                        )}
                                        {hasPermission("destination-itinerary-store", "delete") && (
                                            <Button size="small" startIcon={<DeleteIcon />} sx={{ color: '#f43f5e', fontWeight: 700, '&:hover': { bgcolor: '#ffe4e6' }, borderRadius: 2 }} onClick={() => handleOpenDelete(note)}>
                                                Delete
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>{currentNote ? "Edit Itinerary" : "Create New Itinerary"}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField label="Itinerary Title" fullWidth variant="outlined" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Destination" fullWidth variant="outlined" value={formData.destination} onChange={(e) => setFormData({ ...formData, destination: e.target.value })} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Sub Destination" fullWidth variant="outlined" value={formData.sub_destination} onChange={(e) => setFormData({ ...formData, sub_destination: e.target.value })} />
                            </Grid>
                        </Grid>
                        
                        <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>Itinerary Days</Typography>
                        
                        {formData.itinerary.map((day, dayIndex) => (
                            <Card key={dayIndex} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                        <Button size="small" color="error" onClick={() => removeDay(dayIndex)}>Remove Day</Button>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12}>
                                            <TextField 
                                                size="small" 
                                                fullWidth 
                                                label="Header Value" 
                                                value={day.headerValue} 
                                                onChange={(e) => handleItineraryChange(dayIndex, 'headerValue', e.target.value)}
                                                placeholder="e.g. Arrival at Bagdogra – Transfer to Gangtok (125 km)"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Distance" value={day.Distance} onChange={(e) => handleItineraryChange(dayIndex, 'Distance', e.target.value)} /></Grid>
                                        <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Duration" value={day.Duration} onChange={(e) => handleItineraryChange(dayIndex, 'Duration', e.target.value)} /></Grid>
                                        <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Altitude" value={day.Altitude} onChange={(e) => handleItineraryChange(dayIndex, 'Altitude', e.target.value)} /></Grid>
                                        <Grid item xs={12} sm={3}><TextField size="small" fullWidth label="Time" value={day.Time} onChange={(e) => handleItineraryChange(dayIndex, 'Time', e.target.value)} /></Grid>
                                    </Grid>
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Paragraphs</Typography>
                                        {day.para.map((paragraph, paraIndex) => (
                                            <Box key={paraIndex} sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                                <Box sx={{ flexGrow: 1, minHeight: '120px', '.ql-editor': { minHeight: '100px', maxHeight: '300px', overflowY: 'auto' } }}>
                                                    <ReactQuill theme="snow" value={paragraph} onChange={(content) => handleParagraphChange(dayIndex, paraIndex, content)} modules={quillModules} formats={quillFormats} />
                                                </Box>
                                                <IconButton color="error" onClick={() => removeParagraph(dayIndex, paraIndex)} sx={{ alignSelf: 'flex-start', mt: 3 }}><DeleteIcon fontSize="small" /></IconButton>
                                            </Box>
                                        ))}
                                        <Button size="small" variant="outlined" onClick={() => addParagraph(dayIndex)} sx={{ mt: 1 }}>Add Paragraph</Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                        <Button variant="dashed" sx={{ border: '1px dashed #ccc', py: 2 }} onClick={addDay} fullWidth>+ ADD ANOTHER DAY</Button>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">Save Itinerary</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth scroll="paper">
                <DialogTitle sx={{ bgcolor: '#f8fafc', fontWeight: 700, color: '#0f172a' }}>{currentNote?.title}</DialogTitle>
                <DialogContent dividers sx={{ bgcolor: '#fff', p: 0 }}>
                    <Box ref={viewContentRef} sx={{ p: 2, bgcolor: '#f8fafc' }}>
                        <Grid container spacing={2}>
                            {currentNote?.itinerary?.map((day, index) => (
                                <Grid item xs={12} key={index}>
                                    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, bgcolor: '#ffffff', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', pr: 4 }}>
                                                    {day.headerValue}
                                                </Typography>
                                                <Tooltip title="Copy this day">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleCopySingleItinerary(day)}
                                                        sx={{ 
                                                            position: 'absolute', 
                                                            top: 16, 
                                                            right: 16,
                                                            color: '#3b82f6',
                                                            bgcolor: '#eff6ff',
                                                            '&:hover': { bgcolor: '#dbeafe' }
                                                        }}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                            
                                            {(day.Distance || day.Duration || day.Altitude || day.Time) && (
                                                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                                    {day.Distance && <Chip size="small" label={`Dist: ${day.Distance}`} sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }} />}
                                                    {day.Duration && <Chip size="small" label={`Dur: ${day.Duration}`} sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }} />}
                                                    {day.Altitude && <Chip size="small" label={`Alt: ${day.Altitude}`} sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }} />}
                                                    {day.Time && <Chip size="small" label={`Time: ${day.Time}`} sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }} />}
                                                </Box>
                                            )}
                                            
                                            {day.para && day.para.length > 0 && (
                                                <Box sx={{ color: '#475569', fontSize: '0.95rem' }}>
                                                    {day.para.map((p, i) => (
                                                        <Box key={i} dangerouslySetInnerHTML={{ __html: p }} sx={{ mb: i !== day.para.length - 1 ? 1.5 : 0 }} />
                                                    ))}
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setViewDialogOpen(false)} color="inherit" sx={{ fontWeight: 600 }}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent><Typography>Are you sure you want to delete "{currentNote?.title}"?</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
            <SnackbarComponent />
        </Container>
    );
};

export default DestinationItineraryStore;
